import React, { createContext, useContext, useState } from 'react';
import { Language, TranslationData } from '../types';
import translationsData from '../translations.json';
import { replaceDatePlaceholders } from '../utils/formatDate';
import { APP_CONFIG } from '../config/settings';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getQuestions: () => Record<string, string[]>;
  getQuestionKeys: () => string[];
  getTotalQuestions: () => number;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: React.ReactNode;
  initialLanguage?: Language;
}

export function LanguageProvider({
  children,
  initialLanguage = APP_CONFIG.DEFAULT_LANGUAGE,
}: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>(initialLanguage);
  const translations = translationsData as TranslationData;

  // Translation function
  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations.ui[language];

    for (const k of keys) {
      value = value?.[k];
    }

    const text = value || key;
    return typeof text === 'string'
      ? replaceDatePlaceholders(text, language)
      : text;
  };

  // Get questions for current language
  const getQuestions = (): Record<string, string[]> => {
    return translations.questions[language] || {};
  };

  // Get question keys in order
  const getQuestionKeys = (): string[] => {
    const questions = getQuestions();
    return Object.keys(questions);
  };

  // Get total number of questions
  const getTotalQuestions = (): number => {
    return getQuestionKeys().length;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    getQuestions,
    getQuestionKeys,
    getTotalQuestions,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
